import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TvCard from '@/components/base/TvCard';
import { tvShows } from '@/mocks/tvShows';

export default function Detail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const show = tvShows.find((s) => s.id === id);

  if (!show) {
    return (
      <>
        <Navbar />
        <main className="pt-32 pb-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-foreground-600 text-4xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-200 mb-2">电视剧未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该电视剧不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-50 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedShows = tvShows
    .filter((s) => s.categorySlug === show.categorySlug && s.id !== show.id)
    .slice(0, 6);

  const pageTitle = `${show.title}全集在线观看 - 夫妻影院`;
  const pageDesc = `${show.title} ${show.englishTitle} 全集免费在线观看，${show.category}高清不卡顿秒播流畅。${show.description.substring(0, 80)}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '夫妻影院首页', item: 'https://www.fuqicinema.com/' },
      { '@type': 'ListItem', position: 2, name: show.category, item: `https://www.fuqicinema.com/category/${show.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: show.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: pageTitle,
    description: pageDesc,
    url: `https://www.fuqicinema.com/detail/${show.id}`,
    author: {
      '@type': 'Organization',
      name: '夫妻影院',
    },
    datePublished: `${show.year}-01-01`,
    dateModified: '2025-01-01',
    about: {
      '@type': 'Thing',
      name: show.title,
      description: show.description,
    },
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${show.title},${show.title}全集,${show.title}在线观看,${show.title}免费高清,${show.category},夫妻影院`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.fuqicinema.com/detail/${show.id}`} />
      <meta property="og:image" content={show.poster} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />
      <main className="pt-20 md:pt-24 pb-10">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">首页</Link>
            <span>/</span>
            <Link to={`/category/${show.categorySlug}`} className="hover:text-accent-500 transition-colors cursor-pointer">
              {show.category}
            </Link>
            <span>/</span>
            <span className="text-foreground-300">{show.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-6 md:gap-10">
            <div className="flex-shrink-0 w-full sm:w-56 lg:w-64 mx-auto sm:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={show.poster}
                  alt={`${show.title} 海报`}
                  title={`${show.title} ${show.category} 海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-3xl font-bold text-foreground-100 font-heading mb-3">
                {show.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{show.englishTitle}</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="bg-accent-500/20 text-accent-500 text-xs px-2.5 py-1 rounded-md font-medium">
                  评分 {show.rating}
                </span>
                <span className="bg-background-200 text-foreground-400 text-xs px-2.5 py-1 rounded-md">
                  {show.category}
                </span>
                <span className="bg-background-200 text-foreground-400 text-xs px-2.5 py-1 rounded-md">
                  {show.year}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-md font-medium ${
                  show.status === '更新中'
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'bg-secondary-500/20 text-secondary-400'
                }`}>
                  {show.status}
                </span>
              </div>

              <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4">
                {show.longDescription}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 text-sm">
                <div>
                  <span className="text-foreground-600">导演：</span>
                  <span className="text-foreground-300">{show.director}</span>
                </div>
                <div>
                  <span className="text-foreground-600">主演：</span>
                  <span className="text-foreground-300">{show.cast.join('、')}</span>
                </div>
                <div>
                  <span className="text-foreground-600">地区：</span>
                  <span className="text-foreground-300">{show.region}</span>
                </div>
                <div>
                  <span className="text-foreground-600">语言：</span>
                  <span className="text-foreground-300">{show.language}</span>
                </div>
                <div>
                  <span className="text-foreground-600">类型：</span>
                  <span className="text-foreground-300">{show.genres.join(' / ')}</span>
                </div>
                <div>
                  <span className="text-foreground-600">集数：</span>
                  <span className="text-foreground-300">{show.totalEpisodes}集</span>
                </div>
                <div>
                  <span className="text-foreground-600">状态：</span>
                  <span className="text-foreground-300">{show.updateInfo}</span>
                </div>
                <div>
                  <span className="text-foreground-600">播放量：</span>
                  <span className="text-foreground-300">{show.views}</span>
                </div>
              </div>

              <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-50 px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-fill text-lg"></i>
                </div>
                立即播放
              </button>
            </div>
          </div>

          <section className="mt-10 md:mt-14" aria-label="分集列表">
            <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-5">
              分集列表
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {show.episodes.map((ep) => (
                <button
                  key={ep.id}
                  className={`px-3 py-2.5 rounded-lg text-xs text-center transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    ep.episodeNumber <= show.currentEpisode
                      ? 'bg-background-200/70 text-foreground-300 hover:bg-accent-500/20 hover:text-accent-500 border border-background-300/40 hover:border-accent-500/40'
                      : 'bg-background-200/30 text-foreground-700 border border-background-300/20 cursor-not-allowed'
                  }`}
                  disabled={ep.episodeNumber > show.currentEpisode}
                >
                  {ep.title}
                </button>
              ))}
            </div>
          </section>

          {relatedShows.length > 0 && (
            <section className="mt-10 md:mt-14" aria-label="相关推荐">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {relatedShows.map((s) => (
                  <TvCard key={s.id} show={s} size="small" />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}